"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "components/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * DialogContent
 *
 * Centering is done by a fixed, full-screen flex wrapper rather than the
 * `left-1/2 top-1/2 -translate-1/2` recipe. The translate approach positions the
 * panel relative to the *initial containing block*, which on mobile grows wider
 * than the visible area as soon as anything on the page overflows horizontally —
 * the panel then drifts off-screen and gets clipped. Flex centering inside a
 * `fixed inset-0` wrapper stays correct regardless.
 *
 * Sizing is mobile-first and baked in here, so call sites don't hand-roll
 * `w-[95vw] h-[90dvh] sm:h-auto` recipes: a full-width bottom sheet on phones,
 * a centered modal from `sm:` up, `dvh`-based max height, and safe-area padding.
 *
 * Compose the body with DialogHeader / DialogBody / DialogFooter — DialogBody is
 * the only scrolling region, so header and footer stay pinned.
 */
const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    {/* Full-screen flex-centering wrapper. `pointer-events-none` lets clicks fall
        through to the overlay below, so click-outside-to-close keeps working. */}
    <div className="pointer-events-none fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "pointer-events-auto relative flex w-full max-w-lg flex-col overflow-hidden",
          "border bg-background shadow-lg",
          // Mobile: full-width sheet pinned to the bottom, rounded top corners only.
          "max-h-[92dvh] rounded-t-lg pb-[env(safe-area-inset-bottom)]",
          // sm+ : ordinary centered modal.
          "sm:max-h-[85dvh] sm:rounded-lg sm:pb-0",
          "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-bottom-2 data-[state=open]:slide-in-from-bottom-2",
          className
        )}
        {...props}>
        {children}
        <DialogPrimitive.Close
          className="absolute end-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">סגור</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </div>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      // `pe-10` keeps the title clear of the absolutely-positioned close button.
      "shrink-0 space-y-1.5 border-b px-6 pb-4 pe-10 pt-6 text-start",
      className
    )}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

/** The dialog's only scrollable region — keeps header and footer pinned. */
const DialogBody = ({
  className,
  ...props
}) => (
  <div
    className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-4", className)}
    {...props} />
)
DialogBody.displayName = "DialogBody"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex shrink-0 flex-col-reverse gap-2 border-t bg-background px-6 py-4 sm:flex-row sm:justify-end",
      className
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
